import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, Save, X } from 'lucide-react';
import useCategoryStore from '../store/categoryStore';
import useUIStore from '../store/uiStore';

const TASK_TYPE_LABELS = {
  checkpoint: '% of weight (checkpoint)',
  count: 'Quantity / count',
  hours: 'Hours only',
};

let tempCounter = 0;
const tempId = () => `temp_${Date.now()}_${tempCounter++}`;
const isTemp = (id) => typeof id === 'string' && id.startsWith('temp_');

const cloneCategories = (categories) =>
  categories.map((c) => ({
    ...c,
    tasks: (c.tasks || []).map((t) => ({
      ...t,
      checkpoints: t.checkpoints ? t.checkpoints.map((cp) => ({ ...cp })) : null,
    })),
  }));

const weightTotal = (category) =>
  (category.tasks || [])
    .filter((t) => t.task_type === 'checkpoint')
    .reduce((sum, t) => sum + (Number(t.weight_percent) || 0), 0);

const CategoryConfigPage = () => {
  const {
    categories, fetchCategories, createCategory, updateCategory, deleteCategory,
    createTask, updateTask, deleteTask,
  } = useCategoryStore();
  const showToast = useUIStore((s) => s.showToast);

  const [local, setLocal] = useState([]);
  const [openIds, setOpenIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategories().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocal(cloneCategories(categories));
    setOpenIds((prev) => {
      const next = new Set(prev);
      categories.forEach((c) => next.add(c.id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const dirty = JSON.stringify(local) !== JSON.stringify(cloneCategories(categories));

  const toggleOpen = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const updateLocalCategory = (id, patch) =>
    setLocal((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const updateLocalTask = (categoryId, taskId, patch) =>
    setLocal((prev) =>
      prev.map((c) =>
        c.id !== categoryId
          ? c
          : { ...c, tasks: c.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) }
      )
    );

  const addCategory = () => {
    const id = tempId();
    setLocal((prev) => [...prev, { id, name: 'New Category', mode: 'site', tasks: [] }]);
    setOpenIds((prev) => new Set(prev).add(id));
  };

  const removeCategory = (id) => setLocal((prev) => prev.filter((c) => c.id !== id));

  const addTask = (categoryId) => {
    const category = local.find((c) => c.id === categoryId);
    const id = tempId();
    const newTask =
      category.mode === 'production'
        ? { id, name: 'New task', task_type: 'checkpoint', weight_percent: 0, unit: null, checkpoints: [{ label: 'Complete', percent: 100 }] }
        : { id, name: 'New task', task_type: 'count', weight_percent: null, unit: 'units', checkpoints: null };
    updateLocalCategory(categoryId, { tasks: [...category.tasks, newTask] });
  };

  const removeTask = (categoryId, taskId) => {
    const category = local.find((c) => c.id === categoryId);
    updateLocalCategory(categoryId, { tasks: category.tasks.filter((t) => t.id !== taskId) });
  };

  const addCheckpoint = (categoryId, taskId) => {
    const task = local.find((c) => c.id === categoryId).tasks.find((t) => t.id === taskId);
    updateLocalTask(categoryId, taskId, { checkpoints: [...(task.checkpoints || []), { label: 'New checkpoint', percent: 100 }] });
  };

  const updateCheckpoint = (categoryId, taskId, idx, patch) => {
    const task = local.find((c) => c.id === categoryId).tasks.find((t) => t.id === taskId);
    const checkpoints = task.checkpoints.map((cp, i) => (i === idx ? { ...cp, ...patch } : cp));
    updateLocalTask(categoryId, taskId, { checkpoints });
  };

  const removeCheckpoint = (categoryId, taskId, idx) => {
    const task = local.find((c) => c.id === categoryId).tasks.find((t) => t.id === taskId);
    updateLocalTask(categoryId, taskId, { checkpoints: task.checkpoints.filter((_, i) => i !== idx) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const localIds = new Set(local.map((c) => c.id));

      for (const c of categories) {
        if (!localIds.has(c.id)) {
          await deleteCategory(c.id);
        }
      }

      for (const c of local) {
        let categoryId = c.id;
        const originalCategory = categories.find((oc) => oc.id === c.id);

        if (isTemp(c.id)) {
          const res = await createCategory({ name: c.name, mode: c.mode });
          if (!res.success) {
            showToast(res.error, 'error');
            continue;
          }
          categoryId = res.data.category_id;
        } else if (originalCategory && (originalCategory.name !== c.name || originalCategory.mode !== c.mode)) {
          await updateCategory(c.id, { name: c.name, mode: c.mode });
        }

        const localTaskIds = new Set(c.tasks.filter((t) => !isTemp(t.id)).map((t) => t.id));
        for (const t of originalCategory?.tasks || []) {
          if (!localTaskIds.has(t.id)) {
            await deleteTask(t.id);
          }
        }

        for (const t of c.tasks) {
          const payload = {
            name: t.name,
            task_type: t.task_type,
            weight_percent: t.task_type === 'checkpoint' ? Number(t.weight_percent) || 0 : null,
            unit: t.task_type === 'checkpoint' ? null : t.unit,
            checkpoints: t.task_type === 'checkpoint' ? t.checkpoints : null,
          };

          if (isTemp(t.id)) {
            const res = await createTask({ category_id: categoryId, ...payload });
            if (!res.success) showToast(res.error, 'error');
          } else {
            const originalTask = (originalCategory?.tasks || []).find((ot) => ot.id === t.id);
            if (originalTask && JSON.stringify(originalTask) !== JSON.stringify(t)) {
              await updateTask(t.id, payload);
            }
          }
        }
      }

      await fetchCategories();
      showToast('Categories updated', 'success');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setLocal(cloneCategories(categories));

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-4xl mx-auto text-gray-500 text-sm">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Categories & Tasks</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure work categories, their production mode, and how progress is measured for each task.
          </p>
        </div>

        <div className="space-y-4">
          {local.map((c) => {
            const total = weightTotal(c);
            return (
              <div key={c.id} className="glass-card p-5">
                <div className="flex items-center gap-3 flex-wrap cursor-pointer" onClick={() => toggleOpen(c.id)}>
                  {openIds.has(c.id) ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                  <input
                    value={c.name}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateLocalCategory(c.id, { name: e.target.value })}
                    className="font-semibold text-gray-900 text-sm border-b border-transparent hover:border-gray-300 focus:border-komal-blue outline-none px-1 py-0.5 bg-transparent"
                  />
                  <select
                    value={c.mode}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateLocalCategory(c.id, { mode: e.target.value })}
                    className="border border-gray-300 rounded px-2 py-1 text-xs"
                  >
                    <option value="production">Production (weight/% based)</option>
                    <option value="site">Site (count/hours based)</option>
                  </select>
                  {c.mode === 'production' && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded uppercase tracking-wide ${total === 100 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {total}% of weight allocated
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeCategory(c.id); }}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                {openIds.has(c.id) && (
                  <div className="mt-4 space-y-3">
                    {c.tasks.map((t) => (
                      <div key={t.id} className="border border-gray-200 rounded p-3 bg-gray-50">
                        <div className="flex gap-2 items-center flex-wrap">
                          <input
                            value={t.name}
                            onChange={(e) => updateLocalTask(c.id, t.id, { name: e.target.value })}
                            className="border border-gray-300 rounded px-2 py-1 text-sm font-medium min-w-[140px]"
                          />
                          <select
                            value={t.task_type}
                            onChange={(e) => {
                              const task_type = e.target.value;
                              if (task_type === 'checkpoint') {
                                updateLocalTask(c.id, t.id, { task_type, unit: null, checkpoints: t.checkpoints || [{ label: 'Complete', percent: 100 }] });
                              } else {
                                updateLocalTask(c.id, t.id, { task_type, weight_percent: null, unit: t.unit || 'units' });
                              }
                            }}
                            className="border border-gray-300 rounded px-2 py-1 text-xs"
                          >
                            {Object.entries(TASK_TYPE_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>

                          {t.task_type === 'checkpoint' ? (
                            <label className="flex items-center gap-1 text-xs text-gray-500">
                              % of total weight
                              <input
                                type="number"
                                value={t.weight_percent ?? ''}
                                onChange={(e) => updateLocalTask(c.id, t.id, { weight_percent: e.target.value })}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-16"
                              />
                            </label>
                          ) : (
                            <label className="flex items-center gap-1 text-xs text-gray-500">
                              Unit label
                              <input
                                value={t.unit ?? ''}
                                onChange={(e) => updateLocalTask(c.id, t.id, { unit: e.target.value })}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-24"
                              />
                            </label>
                          )}

                          <button onClick={() => removeTask(c.id, t.id)} className="ml-auto text-red-500 hover:text-red-700">
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {t.task_type === 'checkpoint' && (
                          <div className="mt-3 pl-1">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Checkpoints (supervisor picks one, not a free % guess)
                            </div>
                            {(t.checkpoints || []).map((cp, idx) => (
                              <div key={idx} className="flex items-center gap-2 mb-1.5">
                                <input
                                  value={cp.label}
                                  onChange={(e) => updateCheckpoint(c.id, t.id, idx, { label: e.target.value })}
                                  className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                                />
                                <input
                                  type="number"
                                  value={cp.percent}
                                  onChange={(e) => updateCheckpoint(c.id, t.id, idx, { percent: Number(e.target.value) })}
                                  className="border border-gray-300 rounded px-2 py-1 text-sm w-16"
                                />
                                <span className="text-xs text-gray-400">%</span>
                                <button onClick={() => removeCheckpoint(c.id, t.id, idx)} className="text-red-500 hover:text-red-700">
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addCheckpoint(c.id, t.id)}
                              className="text-xs text-komal-blue hover:underline flex items-center gap-1 mt-1"
                            >
                              <Plus size={11} /> Add checkpoint
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addTask(c.id)}
                      className="text-sm text-komal-blue hover:underline flex items-center gap-1"
                    >
                      <Plus size={13} /> Add task
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          onClick={addCategory}
          className="mt-4 px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1"
        >
          <Plus size={14} /> Add category
        </button>

        {dirty && (
          <div className="sticky bottom-0 bg-gray-100 pt-4 mt-4 flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60 flex items-center gap-2"
            >
              <Save size={14} /> {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button
              onClick={handleDiscard}
              disabled={saving}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Discard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryConfigPage;
